import { useState } from "react";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import { ChatToolType } from "_graphql-types-frontend/graphql";
import { ChatMessage, ToolCallEntry } from "../ChatBox/types";
import { LOAD_CONVERSATION } from "./graphql";
import { agentDisplayName } from "../ChatBox/agentName";

/**
 * A persistent agent's reply, lifted out of its tool card into the transcript
 * as a message in its own voice.
 *
 * Persistent agents only: an agent with an ongoing thread is a participant the
 * user returns to, so seeing HOW it argues matters. Ephemeral and ad-hoc
 * sub-agents are the assistant's working steps and stay in the card.
 *
 * Derived from the spawn's tool result — which is streamed live and persisted
 * in chatContext — so the same message appears while chatting and after a
 * reload with no schema of its own.
 */
export function speakerMessages(
  tools: ToolCallEntry[],
  idPrefix: string
): ChatMessage[] {
  const spawns = tools.filter(
    t => t.tool === ChatToolType.SpawnSubAgentTool && !t.toolName
  );
  const messages: ChatMessage[] = [];
  spawns.forEach((spawn, i) => {
    const results = spawn.results as SpawnResults | undefined;
    const params = spawn.params as { agent?: string; mode?: string } | undefined;
    const id = `${idPrefix}-speaker-${spawn.toolCallId ?? i}`;

    if (!results) {
      // Still running. Only placeholder what is KNOWN to be persistent — an
      // agent whose mode comes from its definition is not known until it ends.
      if (params?.agent && params.mode === "persistent") {
        messages.push({
          type: "agent",
          id,
          speaker: { key: params.agent, name: agentDisplayName(params.agent), note: "thinking…" },
          data: {
            type: "react",
            message: <CircularProgress size={16} color="primary" />,
          },
        });
      }
      return;
    }

    const persistent = results.mode === "persistent" || results.resumedThread === true;
    const text = results.subAgentResult?.trim();
    if (!persistent || !results.agent || results.error || !text) return;

    const speaker = {
      // The handle keys the speaker (its color, its grouping); people see
      // the display name.
      key: results.agent,
      name: agentDisplayName(results.agent),
      note: results.resumedThread ? "continuing thread" : "new thread",
      ...(results.color ? { color: results.color } : {}),
    };
    messages.push({ type: "agent", id, speaker, data: { type: "markdown", message: text } });
    // The thread, on demand — a separate message from the same speaker, so it
    // sits under the same head and the reply itself stays plain markdown.
    if (results.threadConversationId != null) {
      messages.push({
        type: "agent",
        id: `${id}-thread`,
        speaker,
        data: {
          type: "react",
          message: <AgentThread threadId={results.threadConversationId} />,
        },
      });
    }
  });
  return messages;
}

type SpawnResults = {
  subAgentResult?: string;
  color?: string;
  threadConversationId?: number;
  agent?: string;
  mode?: string;
  resumedThread?: boolean;
  error?: string;
};

/**
 * A persistent agent's whole thread, opened in place.
 *
 * The agent remembers what it argued before, and that history is the point of
 * a persistent agent. Loaded only when asked for, through loadConversation,
 * which is owner-checked like every other conversation read (a thread is the
 * user's own conversation row). Plain text rather than markdown: this is a
 * record to scan, and it keeps the markdown renderer out of this module.
 */
export function AgentThread({ threadId }: { threadId: number }) {
  const [open, setOpen] = useState(false);
  const { data, loading, error } = useQuery(LOAD_CONVERSATION, {
    variables: { conversationId: threadId },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const exchanges = data?.loadConversation ?? [];

  return (
    <div data-testid="agent-thread-toggle">
      <ButtonBase
        data-testid="toggle-agent-thread"
        onClick={() => setOpen(o => !o)}
        sx={{ fontSize: 12, color: "text.secondary", "&:hover": { color: "text.primary" } }}
      >
        {open ? "Hide thread" : "Show full thread"}
      </ButtonBase>
      {open && (
        <Box data-testid="agent-thread" sx={{ mt: 1, fontSize: 13 }}>
          {loading && exchanges.length === 0 && <CircularProgress size={14} />}
          {error && (
            <Box sx={{ color: "error.main" }}>Could not load this thread: {error.message}</Box>
          )}
          {exchanges.map((x, i) => (
            <Box
              key={x.id}
              sx={{ py: 1, borderTop: i === 0 ? "none" : "1px solid", borderColor: "divider" }}
            >
              <Box sx={{ color: "text.secondary", fontSize: 12, mb: 0.5 }}>
                Asked: {x.question}
              </Box>
              <Box sx={{ whiteSpace: "pre-wrap" }}>{x.answer}</Box>
            </Box>
          ))}
        </Box>
      )}
    </div>
  );
}
